<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

import { useDialogListener } from '@/composables/useEventBasedDialog';
import checkoutService from '@/services/checkoutService';
import { useWarehouseInfo } from '@/composables/useWarehouseInfo';
import useNotify from '@/composables/useNotify';
import { CheckoutFlowStep, useCheckoutFlow } from '@/composables/useCheckoutFlow';
import { isValidEmail, isValidPhone } from '@/lib/utils';

const {
  isDialogOpen,
  closeDialog,
  resetDialogProps,
  subscribeToDialogEvent,
  unsubscribeFromDialogEvent
} = useDialogListener('open-complete-checkout-dialog');

const { warehouseData } = useWarehouseInfo();
const checkoutFlow = useCheckoutFlow();
const { notify } = useNotify();

const showSendBolSection = ref(false);
const isSendEmailEnabled = ref(false);
const isSendSmsEnabled = ref(false);
const isCompleteCheckoutEnabled = ref(false);
const isLoading = ref(false);
const email = ref('');
const phoneNumber = ref('');
const isDocumentLoading = ref(false);
const bolDocumentString = ref<string>('');

const isOpen = computed(() => isDialogOpen.value);

const fetchBolDocument = async () => {
  isDocumentLoading.value = true;
  try {
    const bolData = await checkoutService.getBol(
      warehouseData.value.id,
      checkoutFlow.appointmentId.value
    );
    const bolPdf = await checkoutService.previewBolPdf(
      warehouseData.value.id,
      bolData.signedDoc.id
    );
    const blob = new Blob([bolPdf], { type: 'application/pdf' });
    bolDocumentString.value = URL.createObjectURL(blob) + '#toolbar=0&navpanes=0&scrollbar=0';
  } catch (error) {
    notify('Error fetching BOL document, please try again later', 'error');
    console.error('Error fetching BOL document:', error);
  } finally {
    isDocumentLoading.value = false;
  }
};

const handlePrintBol = async () => {
  try {
    await fetchBolDocument();

    let iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    iframe.onload = function () {
      setTimeout(function () {
        iframe.focus();
        iframe.contentWindow.print();
      }, 1);
    };

    iframe.src = bolDocumentString.value;
    isCompleteCheckoutEnabled.value = true;
  } catch (error) {
    notify('An error occurred while preparing the BOL for printing. Please try again.', 'error');
    console.error('Error preparing BOL for printing:', error);
  }
};

const handleFinishCheckout = async () => {
  isLoading.value = true;
  try {
    await checkoutService.completeCheckout(warehouseData.value.id, {
      appointmentId: checkoutFlow.appointmentId.value,
      assetVisitId: checkoutFlow.assetVisitId.value
    });

    closeDialog();
    checkoutFlow.setNextStep(CheckoutFlowStep.CheckoutCompletePage);
  } catch (error) {
    notify('An error occurred while finishing checkout. Please try again.', 'error');
    console.error('Error finishing checkout:', error);
  } finally {
    isLoading.value = false;
  }
};

const handleSendBolEmail = async () => {
  isLoading.value = true;
  try {
    await checkoutService.sendBolEmailSms({
      warehouseId: warehouseData.value.id,
      appointmentId: checkoutFlow.appointmentId.value,
      contactData: email.value,
      type: 'email'
    });

    notify('BOL sent successfully to email', 'success');
  } catch (error) {
    notify('An error occurred while sending BOL to email. Please try again.', 'error');
    console.error('Error sending BOL to email:', error);
  } finally {
    isLoading.value = false;
  }
};

const handleSendBolSms = async () => {
  isLoading.value = true;
  try {
    await checkoutService.sendBolEmailSms({
      warehouseId: warehouseData.value.id,
      appointmentId: checkoutFlow.appointmentId.value,
      contactData: phoneNumber.value,
      type: 'sms'
    });

    notify('BOL sent successfully to phone number', 'success');
  } catch (error) {
    notify('An error occurred while sending BOL to phone number. Please try again.', 'error');
    console.error('Error sending BOL to phone number:', error);
  } finally {
    isLoading.value = false;
  }
};

const revokeObjectUrl = () => {
  if (bolDocumentString.value && bolDocumentString.value.startsWith('blob:')) {
    URL.revokeObjectURL(bolDocumentString.value.split('#')[0]);
  }
};

const onAfterLeave = () => {
  resetDialogProps();
  revokeObjectUrl();
};

watch(email, newVal => {
  isSendEmailEnabled.value = isValidEmail(newVal);
});

watch(phoneNumber, newVal => {
  isSendSmsEnabled.value = isValidPhone(newVal);
});

onMounted(async () => {
  subscribeToDialogEvent();
});

onUnmounted(() => {
  unsubscribeFromDialogEvent();
  revokeObjectUrl();
});
</script>

<template>
  <v-dialog v-model="isOpen" persistent max-width="625" @after-leave="onAfterLeave">
    <v-card :rounded="false">
      <div class="d-flex flex-row align-center p-4 complete-checkout-bol-signed">
        <v-icon size="large" color="success" class="mr-2">mdi-check-circle-outline</v-icon>
        <p class="typography-heading-md">BOL Successfully Signed</p>
      </div>

      <div class="d-flex flex-column gap-8 p-5 mt-5">
        <p class="typography-heading-xlg-bold">
          Please print the signed BOL in order to finish the check-out.
        </p>

        <v-btn
          prepend-icon="mdi-printer"
          color="primary"
          @click="handlePrintBol"
          :disabled="isDocumentLoading"
          :loading="isDocumentLoading">
          Print Signed BOL
        </v-btn>

        <p class="d-flex justify-center">
          If you have any issues printing, please call the office for assistance.
        </p>
      </div>

      <div class="d-flex flex-column gap-5 p-5">
        <v-divider></v-divider>

        <v-btn
          variant="text"
          size="small"
          class="complete-checkout-btn"
          @click="showSendBolSection = !showSendBolSection">
          Need to send the signed BOL?
        </v-btn>

        <div
          v-if="showSendBolSection"
          class="d-flex flex-column p-5 complete-checkout-send-bol-section">
          <v-row class="align-center">
            <v-col cols="9">
              <v-label class="typography-body-md-bold mb-2">Send signed BOL via email</v-label>
              <v-text-field
                variant="outlined"
                density="default"
                bg-color="white"
                placeholder="e.g. driver@example.com"
                v-model="email"></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-btn
                prepend-icon="mdi-email-outline"
                color="primary"
                size="large"
                class="mt-1"
                :disabled="!isSendEmailEnabled || isLoading"
                :loading="isLoading"
                @click="handleSendBolEmail">
                Send
              </v-btn>
            </v-col>
          </v-row>
          <v-row class="align-center">
            <v-col cols="9">
              <v-label class="typography-body-md-bold mb-2">Send signed BOL via SMS</v-label>
              <v-text-field
                variant="outlined"
                density="default"
                placeholder="e.g. +1 (555) 123-4567"
                bg-color="white"
                type="tel"
                v-model="phoneNumber"></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-btn
                prepend-icon="mdi-message-outline"
                color="primary"
                size="large"
                class="mt-1"
                :disabled="!isSendSmsEnabled || isLoading"
                :loading="isLoading"
                @click="handleSendBolSms">
                Send
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <v-btn color="primary" :disabled="!isCompleteCheckoutEnabled" @click="handleFinishCheckout">
          Finish Checkout
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.complete-checkout-bol-signed {
  background: $color-success-20;
}
.complete-checkout-btn {
  color: $color-text-link;
}
.complete-checkout-send-bol-section {
  background-color: $color-background-secondary;
}
</style>
